// Process Flow Types for PFMEA
// AIAG-VDA compliant process flow diagram data structures

import { Node, Edge } from 'reactflow';

// ============================================================
// PROCESS STEP TYPES - Standard AIAG process flow symbols
// ============================================================
export enum ProcessStepType {
    OPERATION = 'operation',           // ⬭ Rectangle with rounded corners
    INSPECTION = 'inspection',         // ⬡ Diamond/Hexagon
    TRANSPORT = 'transport',           // ➜ Arrow
    STORAGE = 'storage',               // ▽ Triangle pointing down
    DELAY = 'delay',                   // D-shape
    DECISION = 'decision',             // ◇ Diamond
    START_END = 'start_end',           // ⬭ Rounded rectangle (terminal)
    SUBPROCESS = 'subprocess',         // □ Rectangle with lines
    SPC_CONTROL = 'spc_control',       // SPC control point
    POKA_YOKE = 'poka_yoke',           // Error-proofing station
}

// ============================================================
// SPECIAL CHARACTERISTICS
// ============================================================
export enum SpecialCharacteristic {
    NONE = 'none',
    CC = 'cc',           // Critical Characteristic - Safety/Regulatory
    SC = 'sc',           // Significant Characteristic - Fit/Function
    STANDARD = 'standard', // Standard characteristic
}

// ============================================================
// 4M CATEGORY (for process element classification)
// ============================================================
export enum ProcessElement4M {
    MAN = 'man',
    MACHINE = 'machine',
    MATERIAL = 'material',
    METHOD = 'method',
}

// ============================================================
// PROCESS STEP DATA
// ============================================================
export interface ProcessStepData {
    // Basic Info
    label: string;
    stepNumber: string;              // e.g., "OP-010", "INS-020"
    description?: string;

    // Step Classification
    type: ProcessStepType;
    element4M?: ProcessElement4M;     // Primary 4M classification

    // Equipment & Location
    equipment?: string;               // Machine or workstation name
    stationId?: string;               // Assembly station reference
    workArea?: string;                // Production area/cell

    // Time & Metrics
    cycleTime?: number;               // Seconds per unit
    setupTime?: number;               // Minutes for changeover

    // Special Characteristics
    specialCharacteristic?: SpecialCharacteristic;

    // Product/Process Characteristic
    characteristic?: string;          // What's being controlled
    specification?: string;           // Tolerance or requirement

    // Control Information
    controlMethod?: string;           // How it's controlled (SPC, 100%, etc.)
    sampleSize?: string;              // n=5, 100%, etc.
    sampleFrequency?: string;         // per hour, per shift, etc.

    // Linkages
    pfmeaItemIds?: string[];          // Linked PFMEA failure modes
    controlPlanItemIds?: string[];    // Linked control plan items
    workInstructionId?: string;       // Linked work instruction

    // UI State
    collapsed?: boolean;

    // Metadata
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
}

// ============================================================
// EDGE/CONNECTION DATA
// ============================================================
export enum ConnectionType {
    DEFAULT = 'default',
    MATERIAL_FLOW = 'material_flow',   // Primary material path
    REWORK = 'rework',                  // Rework loop
    REJECT = 'reject',                  // Reject path
    ALTERNATE = 'alternate',            // Alternate process path
    INFORMATION = 'information',        // Information flow
}

export interface ProcessEdgeData {
    connectionType?: ConnectionType;
    label?: string;
}

// ============================================================
// REACT FLOW NODE & EDGE TYPES
// ============================================================
export type ProcessFlowNode = Node<ProcessStepData>;
export type ProcessFlowEdge = Edge<ProcessEdgeData>;

// ============================================================
// PROCESS FLOW DIAGRAM STRUCTURE
// ============================================================
export interface ProcessFlowDiagram {
    id: string;
    name: string;
    description?: string;
    projectId: string;

    // AIAG-VDA 7-Step Reference
    aiagVdaStep: number;              // Which step (2=Structure, 3=Function)

    // Flow Data
    nodes: ProcessFlowNode[];
    edges: ProcessFlowEdge[];

    // Metadata
    version: string;
    status: 'draft' | 'review' | 'approved';
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    approvedBy?: string;
    approvedAt?: string;
}

// ============================================================
// AIAG-VDA TEMPLATES
// ============================================================
export interface ProcessFlowTemplate {
    id: string;
    name: string;
    description: string;
    industry: string;
    nodes: ProcessFlowNode[];
    edges: ProcessFlowEdge[];
}

// Default step number generator
export function generateStepNumber(type: ProcessStepType, index: number): string {
    const prefixes: Record<ProcessStepType, string> = {
        [ProcessStepType.OPERATION]: 'OP',
        [ProcessStepType.INSPECTION]: 'INS',
        [ProcessStepType.TRANSPORT]: 'TR',
        [ProcessStepType.STORAGE]: 'ST',
        [ProcessStepType.DELAY]: 'DL',
        [ProcessStepType.DECISION]: 'DEC',
        [ProcessStepType.START_END]: '',
        [ProcessStepType.SUBPROCESS]: 'SUB',
        [ProcessStepType.SPC_CONTROL]: 'SPC',
        [ProcessStepType.POKA_YOKE]: 'PY',
    };

    const num = String(index * 10).padStart(3, '0');
    return prefixes[type] ? `${prefixes[type]}-${num}` : '';
}

// Color mapping for step types
export const STEP_TYPE_COLORS: Record<ProcessStepType, { bg: string; border: string; text: string }> = {
    [ProcessStepType.OPERATION]: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },      // Blue
    [ProcessStepType.INSPECTION]: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },     // Amber
    [ProcessStepType.TRANSPORT]: { bg: '#e0e7ff', border: '#6366f1', text: '#3730a3' },      // Indigo
    [ProcessStepType.STORAGE]: { bg: '#f3e8ff', border: '#a855f7', text: '#6b21a8' },        // Purple
    [ProcessStepType.DELAY]: { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },          // Pink
    [ProcessStepType.DECISION]: { bg: '#dcfce7', border: '#22c55e', text: '#166534' },       // Green
    [ProcessStepType.START_END]: { bg: '#f1f5f9', border: '#64748b', text: '#1e293b' },      // Slate
    [ProcessStepType.SUBPROCESS]: { bg: '#e0f2fe', border: '#0ea5e9', text: '#0369a1' },     // Sky
    [ProcessStepType.SPC_CONTROL]: { bg: '#fef9c3', border: '#eab308', text: '#854d0e' },    // Yellow
    [ProcessStepType.POKA_YOKE]: { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c' },      // Red
};

// Connection type colors
export const CONNECTION_COLORS: Record<ConnectionType, string> = {
    [ConnectionType.DEFAULT]: '#64748b',
    [ConnectionType.MATERIAL_FLOW]: '#3b82f6',
    [ConnectionType.REWORK]: '#f59e0b',
    [ConnectionType.REJECT]: '#ef4444',
    [ConnectionType.ALTERNATE]: '#22c55e',
    [ConnectionType.INFORMATION]: '#8b5cf6',
};
